import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AttestationKind = 'license_attestation' | 'ip_upload_attestation';

export interface AttestationTextVersion {
  id: string;
  kind: AttestationKind;
  version: number;
  body_text: string;
  published_at: string;
  is_current: boolean;
}

export interface HubTemplate {
  id: string;
  tenant_id: null;
  name: string;
  description: string | null;
  framework: string | null;
  vendor_type_target: string[];
  version: string;
  content_revision: number;
  is_hub_template: true;
  hub_template_id: null;
  hub_version_at_import: null;
  requires_license: boolean;
  // Licensed-Content Slice 2 (D-LC.2-5): renamed is_license_attested → has_license_grant.
  has_license_grant: boolean;
  tenant_imported_template_id: string | null;
  // Assessment Lines Slice C — the live line for this template (NULL for ungrouped). Client groups by line_id.
  line_id: string | null;
  line_name: string | null;
  line_description: string | null;
  line_sort_order: number | null;
  is_ai_generated: boolean;
  total_questions: number;
  total_max_score: number;
  pass_threshold_pct: number;
  conditional_threshold_pct: number;
  download_format: string[];
  created_at: string;
  updated_at: string;
  deleted_at: null;
}

export interface TemplateQuestion {
  id: string;
  question_text: string;
  guidance_text: string | null;
  display_order: number;
  response_type: string;
  max_score: number;
  is_required: boolean;
  bypass_eligibility?: { cert_types: string[] };
}

export interface TemplateSection {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  weight_pct: number;
  questions: TemplateQuestion[];
}

export interface TemplateDetail {
  id: string;
  name: string;
  description: string | null;
  framework: string | null;
  version: string;
  content_revision: number;
  is_hub_template: boolean;
  hub_template_id: string | null;
  total_questions: number;
  pass_threshold_pct: number;
  lifecycle_state: string;
  allow_bypass: boolean;
  source_label: string;
  created_at: string;
  updated_at: string;
  sections: TemplateSection[];
}

export interface TenantTemplateLicense {
  id: string;
  tenant_id: string;
  hub_template_id: string;
  attested_at: string;
  attested_by_user_id: string;
  attestation_text_version_id: string;
  license_reference: string | null;
  created_at: string;
}

export interface IpAttestation {
  id: string;
  tenant_id: string;
  attested_at: string;
  attested_by_user_id: string;
  attestation_text_version_id: string;
  source_description: string;
  resulting_template_id: string | null;
  created_at: string;
}

// ── Hub templates ─────────────────────────────────────────────────────────────

export function useHubTemplates() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['hub-templates', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/assessment-templates/hub');
      return data.data as HubTemplate[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60_000,
  });
}

// ── Template detail ───────────────────────────────────────────────────────────

export function useTemplateDetail(templateId: string | undefined) {
  return useQuery({
    queryKey: ['assessment-template', templateId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/assessment-templates/${templateId}`);
      return data.data as TemplateDetail;
    },
    enabled: !!templateId,
    staleTime: 5 * 60_000,
  });
}

// ── Current attestation text ──────────────────────────────────────────────────

export function useCurrentAttestationText(kind: AttestationKind | null) {
  return useQuery({
    queryKey: ['attestation-text', kind],
    queryFn: async () => {
      const { data } = await apiClient.get(`/attestation-texts/current?kind=${kind}`);
      return data.data as AttestationTextVersion;
    },
    enabled: !!kind,
    staleTime: 5 * 60_000,
  });
}

// ── License attestation — RETIRED (Licensed-Content Slice 2) ────────────────────
// useAttestLicense + the LicenseAttestationModal were retired: gated import now requires a
// tenant_template_grants grant (admin-created), not a tenant attestation. The 403
// (LICENSE_GRANT_REQUIRED) surfaces the contact-for-licensing message in HubTemplatesPage.

// ── Hub template import ───────────────────────────────────────────────────────

export function useImportHubTemplate() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (hubTemplateId: string) => {
      const { data } = await apiClient.post(`/assessment-templates/${hubTemplateId}/import`, {});
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-templates', tenantId] });
      qc.invalidateQueries({ queryKey: ['hub-templates', tenantId] });
    },
  });
}

// ── IP upload attestation ─────────────────────────────────────────────────────

export function useCreateIpAttestation() {
  return useMutation({
    mutationFn: async (payload: { attestation_text_version_id: string; source_description: string }) => {
      const { data } = await apiClient.post('/attestations/ip-upload', payload);
      return data.data as IpAttestation;
    },
  });
}

// ── Create tenant template (with attestation_id) ──────────────────────────────

export function useCreateTemplateWithAttestation() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      framework?: string;
      attestation_id: string;
    }) => {
      const { data } = await apiClient.post('/assessment-templates', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-templates', tenantId] });
    },
  });
}
